import {
  doc,
  Firestore,
  getDoc,
  serverTimestamp,
  setDoc,
  updateDoc,
} from 'firebase/firestore';
import { assertUserProfile, type UserProfile } from '@penticton-dev-events/shared';

const usersCollectionPath = 'users';

export const getUserProfile = async (
  firestore: Firestore,
  uid: string,
): Promise<UserProfile | null> => {
  const docRef = doc(firestore, usersCollectionPath, uid);
  const snapshot = await getDoc(docRef);

  if (!snapshot.exists()) {
    return null;
  }

  const raw = snapshot.data() as Record<string, unknown>;

  // Convert Firestore Timestamp to ISO string if present
  const createdAt = raw.createdAt
    ? (raw.createdAt as { toDate: () => Date }).toDate().toISOString()
    : undefined;

  const normalised = {
    uid: snapshot.id,
    ...raw,
    photoURL: raw.photoURL ?? undefined,
    createdAt,
    isAdmin: raw.isAdmin ?? false, // Default to false if not set
    accountStatus: raw.accountStatus ?? 'active', // Default to active if not set
  };

  return assertUserProfile(normalised);
};

export type CreateUserProfilePayload = {
  readonly uid: string;
  readonly displayName: string | null;
  readonly email: string | null;
  readonly photoURL: string | null;
};

export const ensureUserProfile = async (
  firestore: Firestore,
  payload: CreateUserProfilePayload,
): Promise<UserProfile> => {
  const existing = await getUserProfile(firestore, payload.uid);

  if (existing) {
    // Check if existing user needs updates
    const docRef = doc(firestore, usersCollectionPath, payload.uid);
    const docSnapshot = await getDoc(docRef);

    if (docSnapshot.exists()) {
      const data = docSnapshot.data();
      const updates: Record<string, unknown> = {};

      // Add isAdmin field if missing
      if (data.isAdmin === undefined) {
        console.log('⚠️ Migrating user document to add isAdmin field:', payload.uid);
        updates.isAdmin = false;
      }

      // Update display name if payload has one and current is placeholder
      if (
        payload.displayName &&
        existing.displayName !== payload.displayName &&
        (existing.displayName === 'Anonymous Member' ||
          existing.displayName === 'Member' ||
          existing.displayName.includes('@')) // Email-based fallback
      ) {
        console.log('⚠️ Updating display name for user:', payload.uid);
        updates.displayName = payload.displayName;
      }

      // Apply updates if any
      if (Object.keys(updates).length > 0) {
        await updateDoc(docRef, updates);
        return { ...existing, ...updates } as UserProfile;
      }
    }

    return existing;
  }

  const docRef = doc(firestore, usersCollectionPath, payload.uid);

  // Generate display name fallback from email if not provided
  let displayName: string = payload.displayName || '';
  if (!displayName && payload.email) {
    // Use email username part as fallback (e.g., "john.doe@example.com" -> "john.doe")
    displayName = payload.email.split('@')[0] || 'Member';
  }
  if (!displayName) {
    displayName = 'Member'; // Last resort fallback
  }

  // Build user profile data, only including photoURL if it exists
  const profileData: Record<string, unknown> = {
    uid: payload.uid,
    displayName,
    email: payload.email ?? `${payload.uid}@users.noreply`,
    isAdmin: false, // New users are not admins by default
  };

  // Only add photoURL if it exists (not null/undefined)
  if (payload.photoURL) {
    profileData.photoURL = payload.photoURL;
  }

  const userProfile: UserProfile = assertUserProfile(profileData);

  const docData: Record<string, unknown> = {
    displayName: userProfile.displayName,
    email: userProfile.email,
    isAdmin: false,
    accountStatus: 'active',
    createdAt: serverTimestamp(),
  };

  // Only add photoURL if it exists
  if (userProfile.photoURL) {
    docData.photoURL = userProfile.photoURL;
  }

  await setDoc(docRef, docData);

  return userProfile;
};

// DEPRECATED: Use admin repository promoteToAdmin/demoteFromAdmin instead
// This function is kept for backward compatibility but should not be used
export const updateUserRole = async (
  firestore: Firestore,
  uid: string,
  _role: unknown,
): Promise<void> => {
  console.warn(
    'updateUserRole is deprecated. Use promoteToAdmin or demoteFromAdmin from adminRepository instead.',
  );
  // No-op - role field has been removed
  void uid;
};
